import React, { useCallback, useContext } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Flex } from '@atlaskit/primitives/compiled';
import { MenuList } from '@atlaskit/navigation-system';
import {
	STAR,
	UNSTAR,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/star.tsx';
import { FavoriteToggleButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/favorite-toggle-button/index.tsx';
import { MoreNavMenuButton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { useNavigationItemActions } from '@atlassian/navigation-drag-and-drop';
import { MEATBALL_DROPDOWN } from '../../constants.tsx';
import { AnalyticContext } from '../../controllers/analytic-context/index.tsx';
import { useDashboardReorderingIds } from '../../controllers/use-dashboard-reordering-ids.tsx';
import type { DashboardPopupMenuRefactored$key } from './__generated__/DashboardPopupMenuRefactored.graphql.ts';

type Props = {
	dashboardData: DashboardPopupMenuRefactored$key;
	currentItems?: Array<{ id: string; label: string }>;
	currentOrder?: Array<{ menuId: string; visible: boolean }>;
	item?: {
		id: string;
		label: string;
	};
};

export const DashboardPopupMenuRefactored = ({
	dashboardData,
	currentItems,
	currentOrder,
	item,
}: Props): React.JSX.Element => {
	const data = useFragment<DashboardPopupMenuRefactored$key>(
		graphql`
			fragment DashboardPopupMenuRefactored on JiraDashboard {
				id @required(action: THROW)
				dashboardId @required(action: THROW)
				favouriteValue {
					isFavourite
				}
			}
		`,
		dashboardData,
	);

	const { fireUIAnalyticForFixButton, fireUIAnalyticForFavouriteToggle } =
		useContext(AnalyticContext);

	const handleNavMenuOpen = useCallback(() => {
		fireUIAnalyticForFixButton({ itemId: MEATBALL_DROPDOWN });
	}, [fireUIAnalyticForFixButton]);

	const isFavourite = Boolean(data?.favouriteValue?.isFavourite);

	const handleFavouriteClick = useCallback(() => {
		fireUIAnalyticForFavouriteToggle({
			starType: isFavourite ? UNSTAR : STAR,
			parentItemId: String(data.dashboardId),
		});
	}, [data.dashboardId, fireUIAnalyticForFavouriteToggle, isFavourite]);

	const MenuItems = useCallback(
		() => (
			<MenuList>
				<FavoriteToggleButton
					entityId={data.id}
					defaultIsStarred={isFavourite}
					entityTypeName="JiraDashboard"
					onClick={handleFavouriteClick}
				/>
				{currentItems && currentOrder && item ? (
					<ReorderActions currentItems={currentItems} currentOrder={currentOrder} item={item} />
				) : null}
			</MenuList>
		),
		[currentItems, currentOrder, data.id, handleFavouriteClick, isFavourite, item],
	);

	return (
		<Flex gap="space.050" alignItems="center">
			<MoreNavMenuButton onOpen={handleNavMenuOpen} MenuItems={MenuItems} />
		</Flex>
	);
};

type ReorderActionsProps = {
	currentItems: Array<{ id: string; label: string }>;
	currentOrder: Array<{ menuId: string; visible: boolean }>;
	item: {
		id: string;
		label: string;
	};
};

const ReorderActions = ({ currentItems, currentOrder, item }: ReorderActionsProps) => {
	const { entityAri, ownerAri } = useDashboardReorderingIds();
	const actions = useNavigationItemActions({
		currentItems,
		currentOrder,
		entityAri,
		ownerAri,
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		getItemId: (i) => (i as { id: string }).id,
		item,
	});
	return actions;
};
